import { Link } from 'react-router-dom'
import { useEditableSection } from '@/hooks/useEditableSection'
import { usePreviewSection } from '@/hooks/usePreviewSection'
import { useEditContext } from '@/contexts/EditContext'
import { EditableText, EditableRichText, EditableImage } from '@/components/editable'
import { Spinner } from '@/components/ui/Spinner'

export function Hero() {
  const { isEditMode, isPreviewMode } = useEditContext()

  // Use different hooks based on mode
  const editableData = useEditableSection('hero')
  const previewData = usePreviewSection('hero')

  // Select the appropriate data source
  const { content, isLoading, updateField } = isPreviewMode
    ? { ...previewData, updateField: () => {} }
    : editableData

  if (isLoading) {
    return (
      <section className="relative bg-gradient-to-br from-primary-600 to-primary-800 text-white min-h-[400px] flex items-center justify-center">
        <Spinner size="lg" />
      </section>
    )
  }

  const backgroundImage = content.background_image as string | undefined
  const title = (content.title as string) || ''
  const subtitle = (content.subtitle as string) || ''
  const ctaLink = (content.cta_link as string) || '/contact'
  const ctaText = (content.cta_text as string) || 'En savoir plus'

  return (
    <section className="relative bg-gradient-to-br from-primary-600 to-primary-800 text-white">
      {/* Background image overlay */}
      {isEditMode ? (
        <EditableImage
          value={backgroundImage || ''}
          onChange={(url) => updateField('background_image', url)}
          className="absolute inset-0 opacity-20"
          aspectRatio="auto"
        />
      ) : (
        backgroundImage && (
          <div
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: `url(${backgroundImage})` }}
          />
        )
      )}

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
        <div className="max-w-3xl">
          {/* Title */}
          <EditableRichText
            value={title}
            onChange={(html) => updateField('title', html)}
            className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight [&_*]:text-white"
            placeholder="Titre principal"
          />

          {/* Subtitle */}
          <div className="mt-6">
            <EditableText
              value={subtitle}
              onChange={(text) => updateField('subtitle', text)}
              as="p"
              className="text-xl md:text-2xl text-white/90"
              placeholder="Sous-titre accrocheur"
            />
          </div>

          {/* CTA */}
          <div className="mt-8 flex items-center gap-4">
            {isEditMode ? (
              <>
                <div className="inline-block px-8 py-4 bg-white text-primary-600 font-semibold rounded-lg">
                  <EditableText
                    value={ctaText}
                    onChange={(text) => updateField('cta_text', text)}
                    as="span"
                    placeholder="Texte du bouton"
                  />
                </div>
                <div className="text-sm text-white/70">
                  Lien:{' '}
                  <EditableText
                    value={ctaLink}
                    onChange={(text) => updateField('cta_link', text)}
                    as="span"
                    className="underline"
                    placeholder="/contact"
                  />
                </div>
              </>
            ) : (
              <Link
                to={ctaLink}
                className="inline-block px-8 py-4 bg-white text-primary-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
              >
                {ctaText}
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
